import { formatAmount } from 'lib/utils/amount';
import { formatDateToCustomDate } from 'lib/utils/date';
import validate from 'lib/utils/validationRules';

import { DOMAIN_TITLE } from '@/.env';

export default {
  isDesktop(state) {
    if (!state.WINDOW_SIZE) {
      return false;
    }

    const r = document.querySelectorAll(':root')[0];
    const s = window.getComputedStyle(r, null).getPropertyValue('font-size');

    return state.WINDOW_SIZE >= parseInt(s) * 64;
  },

  getSuccessUrl(state, getters) {
    const successUrl = state.TRANSACTION.successUrl || state.PROJECT.success_url || null;

    if (validate.isNull(successUrl)) {
      return getters.getProjectUrl;
    }

    return successUrl;
  },

  getFailUrl(state) {
    const failUrl = state.TRANSACTION.failUrl || state.PROJECT.fail_url || null;

    if (validate.isNull(failUrl)) {
      return '';
    }

    return failUrl;
  },

  getProjectUrl(state) {
    return state.TRANSACTION.projectUrl || state.PROJECT.project_url;
  },

  getProjectCode(state) {
    return state.TRANSACTION.projectCode || state.PROJECT.project_code;
  },

  isDeviceSize(state) {
    if (!state.WINDOW_SIZE) {
      return false;
    }

    return {
      mobile: state.WINDOW_SIZE < 480,
      mobilePlus: state.WINDOW_SIZE < 768,
      laptop: state.WINDOW_SIZE > 767,
      tablet: state.WINDOW_SIZE < 1025,
      desktop: state.WINDOW_SIZE > 1024,
    };
  },

  getPaymentId(state) {
    return state.TRANSACTION.id || sessionStorage.getItem('transactionId') || sessionStorage.getItem('paymentId');
  },

  getProcessToken(state) {
    return state.TRANSACTION.processToken || sessionStorage.getItem('processToken');
  },

  getDisplayPaymentId(state, getters) {
    if (!state.PROJECT.partner_payment_id) {
      if (state.TRANSACTION.partnerPaymentId) {
        return state.TRANSACTION.partnerPaymentId;
      }

      return getters.getPaymentId;
    }

    if (state.PROJECT.multiBill) {
      return getters.getPaymentId;
    }

    return state.PROJECT.partner_payment_id || getters.getPaymentId;
  },

  getApplePaySession(state) {
    return state.APPLE_PAY_SESSION;
  },

  project(state) {
    return state.PROJECT;
  },

  getTransaction(state) {
    return state.TRANSACTION;
  },

  getDisplayAmount(state) {
    const { amount, currency } = state.TRANSACTION;

    return formatAmount(amount.amount + amount.commission, currency);
  },

  getDisplayProjectName(state) {
    return state.PROJECT.project_name || state.TRANSACTION.projectName;
  },

  getDisplayMerchantEmail(state) {
    return state.PROJECT.email || state.TRANSACTION.merchantEmail;
  },

  getPaymentEnabledOptions(state) {
    return state.PROJECT.availablePaymentOptions.filter(option => option.enabled);
  },

  isYandexPayEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'YANDEX');
    return paymentOption && paymentOption.enabled;
  },
  isApplePayEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'APPLE');
    return paymentOption && paymentOption.enabled;
  },
  isGooglePayEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'GOOGLE');
    return paymentOption && paymentOption.enabled;
  },
  isCardPaymentEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'CARD');
    return paymentOption && paymentOption.enabled;
  },
  isQiwiPaymentEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'QIWI');
    return paymentOption && paymentOption.enabled;
  },
  isYooMoneyPaymentEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'YOOMONEY');
    return paymentOption && paymentOption.enabled;
  },
  isPayMePaymentEnabled(state, getters) {
    const paymentOption = getters.getPaymentEnabledOptions.find(option => option.name === 'PAYME');
    return paymentOption && paymentOption.enabled;
  },

  getProvider(state) {
    const { PROJECT, TRANSACTION } = state;

    return PROJECT.provider || TRANSACTION.provider;
  },

  isExternalCheckoutProvider(state, getters) {
    const { getProvider } = getters;

    if (!getProvider) {
      return false;
    }

    return getProvider.code === '97ff3d29-4500-405f-98e2-d9f18bc423d1';
  },

  isPrimaryUzbekistanProvider(state, getters) {
    const { getProvider } = getters;

    if (!getProvider) {
      return false;
    }

    return getProvider.code === '3d287eaa-a175-44fa-b29b-8707c1286c78';
  },

  isPrimaryRussianProvider(state, getters) {
    const { getProvider } = getters;

    if (!getProvider) {
      return false;
    }

    return getProvider.code === 'fca65e5f-1c1c-4fa4-9236-3f236c7ecd54';
  },

  isExternalRedirect(state, getters) {
    const { getProvider } = getters;

    if (!getProvider) {
      return false;
    }

    return getProvider.integrationType === 'external_redirect';
  },

  getOfferta(state, getters) {
    const { getExternalOrigin, getProvider } = getters;

    if (getExternalOrigin) {
      return getExternalOrigin.offerLink;
    }

    if (getProvider) {
      return getProvider.params.offer;
    }

    return null;
  },

  getPolicyPrivacy(state, getters) {
    const { getExternalOrigin, getProvider } = getters;

    if (getExternalOrigin) {
      return getExternalOrigin.privacyPolicyLink;
    }

    if (getProvider) {
      return getProvider.params.privacyPolicy;
    }

    return null;
  },

  getAgreement(state, getters) {
    const { getExternalOrigin, getProvider } = getters;

    if (getExternalOrigin) {
      return getExternalOrigin.termsOfUseLink;
    }

    if (getProvider) {
      return getProvider.params.termsOfUse;
    }

    return null;
  },

  getLanguage(state) {
    const { PROJECT_LOCALE } = state;

    return PROJECT_LOCALE && PROJECT_LOCALE.language;
  },

  getLocalization(state) {
    const { PROJECT_LOCALE } = state;

    return PROJECT_LOCALE && PROJECT_LOCALE.localization;
  },

  getRouteWithParamsByTransaction(state) {
    const routeName = state.TRANSACTION.billId ? 'BillAmount' : 'PaymentProject';
    let params = {};

    params = {
      name: routeName,
      params: { id: state.TRANSACTION_KEY },
    };

    if (Object.keys(state.TRANSACTION_QUERY).length > 0) {
      params.query = state.TRANSACTION_QUERY;
    }

    return params;
  },

  getFormattedPaymentDate(state) {
    return formatDateToCustomDate(state.TRANSACTION.created_at, 'DD-MM-YYYY');
  },

  getSubscription(state) {
    const { billTariff: tariff } = state.TRANSACTION;

    if (validate.isUndefined(tariff) || validate.isNull(tariff)) {
      return null;
    }

    if (!validate.isObjectWithFields(tariff, ['subscription'])) {
      return null;
    }

    return tariff.subscription;
  },

  isSafeDeal(state) {
    return state.PROJECT.safeDeal;
  },

  getAvailablePaymentSystems(state) {
    const { PROJECT } = state;

    if (!validate.isObjectWithFields(PROJECT, ['provider'])) {
      return [];
    }

    return PROJECT.provider.paymentSystems;
  },

  isСheckoutPay(state) {
    const { TRANSACTION } = state;

    if (!TRANSACTION.provider) {
      return false;
    }

    return TRANSACTION.provider.checkoutPay || false;
  },

  getShopKey(state) {
    const { TRANSACTION } = state;

    if (!TRANSACTION.provider) {
      return false;
    }

    return TRANSACTION.provider.shopKey || null;
  },

  getExternalOrigin(state) {
    const { externalOrigin } = state.PROJECT;

    return externalOrigin || null;
  },

  getCompanyName(state, getters) {
    const { getExternalOrigin, getProvider, isInternalOrigin } = getters;

    if (getExternalOrigin) {
      return getExternalOrigin.companyName;
    }

    if (isInternalOrigin && getProvider) {
      return getProvider.params.company;
    }

    return null;
  },

  getCopyright(state, getters) {
    const { getExternalOrigin, getProvider, isInternalOrigin } = getters;

    if (getExternalOrigin) {
      return null;
    }

    if (isInternalOrigin && getProvider) {
      return getProvider.params.copyright;
    }

    return null;
  },

  getPaymentOrigin(state) {
    return state.PAYMENT_ORIGIN;
  },

  isInternalOrigin() {
    const { hostname } = window.location;
    return ['localhost', 'get.stage.capusta.space', 'get.capusta.space'].includes(hostname);
  },

  getLogoLink(state, getters) {
    const { isInternalOrigin, getExternalOrigin } = getters;

    if (isInternalOrigin) {
      return require('@/assets/images/capusta-logo-compressed--black.svg');
    }

    if (validate.isNull(getExternalOrigin)) {
      return null;
    }

    return getExternalOrigin.logoLink;
  },

  isDigitalContent(state) {
    const { billTariff } = state.TRANSACTION;

    if (validate.isNull(billTariff)) {
      return false;
    }

    if (validate.isNull(billTariff.arrangement)) {
      return validate.isNotNull(billTariff.contentUrl);
    }

    return validate.isNotNull(billTariff.arrangement);
  },

  getPageTitle(state, getters) {
    const { isInternalOrigin, getExternalOrigin } = getters;

    if (isInternalOrigin) {
      return DOMAIN_TITLE;
    }

    if (validate.isNull(getExternalOrigin)) {
      return null;
    }

    return getExternalOrigin.title;
  },

  isPaymentAutoRedirection(state, getters) {
    const {
      status,
      failUrl,
      successUrl,
      paymentAutoRedirection,
    } = state.TRANSACTION;

    if (status === 'SUCCESS') {
      if (getters.isDigitalContent || validate.isNull(successUrl)) {
        return false;
      }

      return paymentAutoRedirection;
    }

    if (validate.isNull(failUrl)) {
      return false;
    }

    return paymentAutoRedirection;
  },

  isCurrencyCrypto(state) {
    if (!validate.isObjectWithFields(state.PROJECT, ['currency'])) {
      return false;
    }

    const { currency } = state.PROJECT;

    return currency.type === 'CRYPTO';
  },
};
